import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronDown,
  Cylinder as CylinderIcon,
  FileText,
  HelpCircle,
  Info,
  Loader2,
  ScrollText,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ContactUsMenu } from '@/components/layout/ContactUsMenu';
import { CylinderTankIcon, tankFromLabel } from '@/components/CylinderTankIcon';
import { formatUsd } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCustomerFeatures } from '@/hooks/useCustomerFeatures';
import {
  useCylinderInventory,
  useCylinderTransactions,
  type CylinderInventoryRow,
  type CylinderTransaction,
} from '@/hooks/useCylinders';
import { fmtMirrorDate } from '@/lib/dates';

import { Loader2 as ChainSwitchSpinner } from 'lucide-react';
import { useGroupAssets } from '@/hooks/useGroupAssets';
import ChainCylinders from '@/pages/chain/ChainCylinders';

function fmtDate(iso: string | null): string {
  return fmtMirrorDate(iso);
}

function SingleCylinders() {
  const { data: features } = useCustomerFeatures();
  const { data: inventory, isLoading } = useCylinderInventory();
  const { data: txns } = useCylinderTransactions();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [modal, setModal] = useState<'terms' | 'faq' | null>(null);

  const rows = useMemo(
    () =>
      (inventory ?? []).filter(
        (r) =>
          r.currentBalance > 0 || r.deliveriesSince > 0 || r.pickupsSince > 0,
      ),
    [inventory],
  );

  const totalTanks = rows.reduce((a, r) => a + r.currentBalance, 0);
  const totalRental = rows.reduce((a, r) => a + r.estimatedCharge, 0);

  if (features && !features.cylinderInventory) {
    return (
      <div className="h-full flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-12 h-12 rounded-xl bg-brix-surface-alt flex items-center justify-center text-slate-900 mx-auto mb-3">
            <CylinderIcon size={22} />
          </div>
          <p className="text-base text-slate-900 tracking-tight leading-relaxed">
            Cylinder inventory isn't enabled for your account. If you rent
            CO₂ or nitrogen cylinders from us and don't see them here,
            reach out and we'll switch it on.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-14 md:pb-0">
      <div className="px-6 md:px-8 pt-6 pb-10 max-w-3xl mx-auto">
        {/* The cylinder "document" — Apple-receipt treatment */}
        <div className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep">
          {/* Navy header band — title + mini CO2 / beer-gas silhouettes
              instead of the generic Lucide cylinder. */}
          <div className="bg-brix-primary-deep px-6 md:px-8 py-4 flex items-start gap-3">
            <div className="flex items-end gap-1 shrink-0 py-0.5">
              <CylinderTankIcon tone="co2" size="mini" />
              <CylinderTankIcon tone="mix" size="mini" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight text-white leading-none">
                Cylinder inventory
              </h2>
              <p className="text-sm text-white/85 tracking-tight mt-1.5 leading-snug">
                Tap the arrow on a tank for the full delivery + pickup
                history since your last rental invoice. For older
                records or the full policy, see the buttons below.
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="px-6 md:px-8 py-10 text-center text-base text-slate-900 tracking-tight">
              <Loader2 size={18} className="animate-spin inline mr-2" />
              Loading inventory…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 md:px-8 py-10 text-center">
              <Info size={22} className="text-slate-900 mx-auto mb-2" />
              <p className="text-base text-slate-900 tracking-tight leading-relaxed max-w-md mx-auto">
                No cylinder rentals on file yet. As soon as you receive a
                cylinder delivery and the first monthly rental invoice
                posts, your inventory will show up here.
              </p>
            </div>
          ) : (
            <>
              {/* Detail rows — each row leads with a small inline tank
                  icon next to the big count number. */}
              <div className="px-6 md:px-8 py-5">
                {rows.map((row, idx) => (
                  <CylinderRow
                    key={row.label}
                    row={row}
                    isLast={idx === rows.length - 1}
                    expanded={expanded === row.label}
                    onToggle={() =>
                      setExpanded(expanded === row.label ? null : row.label)
                    }
                    transactions={(txns ?? []).filter((t) => t.label === row.label)}
                  />
                ))}
              </div>

              {/* Totals strip — amounts dominant, labels recede */}
              <div className="px-6 md:px-8 py-4 border-t-2 border-brix-primary-deep bg-brix-surface-alt/40 flex items-baseline justify-between gap-4">
                <div>
                  <div className="text-sm tracking-tight text-slate-900 font-semibold">
                    Total
                  </div>
                  <div
                    className="font-mono text-3xl font-bold text-slate-900 tracking-tight leading-none mt-1"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {totalTanks}{' '}
                    <span className="text-sm font-semibold tracking-tight">
                      {totalTanks === 1 ? 'tank' : 'tanks'} on rent
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm tracking-tight text-slate-900 font-semibold">
                    Est. monthly rental
                  </div>
                  <div
                    className="font-mono text-3xl font-bold text-brix-primary tracking-tight leading-none mt-1"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatUsd(totalRental)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Resources card — just two buttons + support menu. */}
        <div className="mt-6 rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep">
          <div className="bg-brix-primary-deep px-6 md:px-8 py-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
              <ScrollText size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight text-white leading-none">
                Policies &amp; help
              </h2>
              <p className="text-sm text-white/85 tracking-tight mt-1.5 leading-snug">
                Read the full rental terms, see common questions, or
                reach the Brix team if you need older records.
              </p>
            </div>
          </div>
          <div className="px-6 md:px-8 pt-6 pb-6">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setModal('terms')}
              >
                <ScrollText size={13} /> Terms &amp; Conditions
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setModal('faq')}
              >
                <HelpCircle size={13} /> FAQ
              </Button>
              <ContactUsMenu variant="inline" triggerLabel="Chat with us" />
            </div>
          </div>
        </div>
      </div>

      {modal === 'terms' && <TermsModal onClose={() => setModal(null)} />}
      {modal === 'faq' && <FaqModal onClose={() => setModal(null)} />}
    </div>
  );
}

/* ----------------------------- row + details ----------------------------- */

function CylinderRow({
  row,
  isLast,
  expanded,
  onToggle,
  transactions,
}: {
  row: CylinderInventoryRow;
  isLast: boolean;
  expanded: boolean;
  onToggle: () => void;
  transactions: CylinderTransaction[];
}) {
  const t = tankFromLabel(row.label);
  return (
    <div className={cn(!isLast && 'border-b border-brix-border pb-3 mb-3')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 group cursor-pointer text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronDown
            size={14}
            className={cn(
              'shrink-0 text-slate-900 transition-transform',
              expanded && 'rotate-180',
            )}
          />
          {/* Small inline tank icon — sized + colored by cylinder */}
          <CylinderTankIcon tone={t.tone} size="inline" />
          <span
            className="font-mono text-3xl font-bold text-slate-900 tracking-tight leading-none"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {row.currentBalance}
          </span>
          <span className="text-lg font-semibold text-slate-900 tracking-tight">
            {row.label}
          </span>
        </div>
        <div className="text-right">
          <div
            className="font-mono text-3xl font-bold text-slate-900 tracking-tight leading-none"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatUsd(row.estimatedCharge)}
          </div>
          <div className="text-sm tracking-tight text-slate-900 font-medium mt-1">
            @ {formatUsd(row.unitPrice)} / mo
          </div>
        </div>
      </button>

      {expanded && <DetailsTable row={row} transactions={transactions} />}
    </div>
  );
}

function DetailsTable({
  row,
  transactions,
}: {
  row: CylinderInventoryRow;
  transactions: CylinderTransaction[];
}) {
  const sorted = [...transactions].sort((a, b) => {
    if (a.kind === 'start' && b.kind !== 'start') return -1;
    if (b.kind === 'start' && a.kind !== 'start') return 1;
    return a.txnDate.localeCompare(b.txnDate);
  });

  return (
    <div className="mt-4 rounded-lg border border-brix-border overflow-hidden">
      <div className="grid grid-cols-[110px_1fr_70px] gap-3 px-3 py-2.5 text-xs font-semibold tracking-tight text-white bg-brix-primary-deep">
        <div>Date</div>
        <div>Activity</div>
        <div className="text-right">Qty</div>
      </div>
      {sorted.map((t, i) => {
        // Both adjustment kinds arrive SIGNED from the view (+up / −down).
        // 'adjustment'        = a driver counted the tanks (CR-ADJ).
        // 'system_adjustment' = the books were corrected so an order could go
        //                       through (SYS-ADJ). Nobody counted anything, so
        //                       it must never read as an audit.
        const isAdj = t.kind === 'adjustment' || t.kind === 'system_adjustment';
        const signedQty =
          t.kind === 'pickup'
            ? `−${t.quantity}`
            : t.kind === 'delivery'
              ? `+${t.quantity}`
              : isAdj
                ? `${t.quantity > 0 ? '+' : '−'}${Math.abs(t.quantity)}`
                : `${t.quantity}`;
        const qtyColor =
          t.kind === 'pickup' || (isAdj && t.quantity < 0)
            ? 'text-red-600'
            : t.kind === 'delivery' || (isAdj && t.quantity > 0)
              ? 'text-emerald-700'
              : 'text-slate-900';
        return (
          <div
            key={`${t.qboInvoiceId}-${i}`}
            className="grid grid-cols-[110px_1fr_70px] gap-3 px-3 py-2.5 text-base border-b border-brix-border last:border-b-0 items-center"
          >
            <div
              className="font-mono text-sm text-slate-900 tracking-tight"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {fmtDate(t.txnDate)}
            </div>
            <div className="min-w-0">
              {t.kind === 'start' ? (
                <div className="flex items-center gap-1.5 text-slate-900 tracking-tight text-sm">
                  <Info size={14} className="text-slate-900 shrink-0" />
                  <span>
                    Starting balance · last rental invoice{' '}
                    {t.docNumber && t.qboInvoiceId && (
                      <Link
                        to={`/invoices/${t.qboInvoiceId}`}
                        className="text-brix-primary font-semibold underline"
                      >
                        {t.docNumber}
                      </Link>
                    )}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-slate-900 tracking-tight text-sm">
                  {t.kind === 'delivery' ? (
                    <ArrowDownCircle size={14} className="text-emerald-700 shrink-0" />
                  ) : t.kind === 'system_adjustment' ? (
                    <Wrench size={14} className="text-amber-600 shrink-0" />
                  ) : t.kind === 'adjustment' ? (
                    <Info size={14} className="text-brix-primary shrink-0" />
                  ) : (
                    <ArrowUpCircle size={14} className="text-red-600 shrink-0" />
                  )}
                  <span>
                    {t.kind === 'delivery'
                      ? 'Delivered'
                      : t.kind === 'system_adjustment'
                        ? 'Records corrected'
                        : t.kind === 'adjustment'
                          ? 'Audit adjustment'
                          : 'Picked up'}{' '}
                    on{' '}
                    {t.qboInvoiceId ? (
                      <Link
                        to={`/invoices/${t.qboInvoiceId}`}
                        className="text-brix-primary font-semibold underline inline-flex items-center gap-1"
                      >
                        <FileText size={11} /> {t.docNumber ?? `#${t.qboInvoiceId}`}
                      </Link>
                    ) : (
                      t.docNumber
                    )}
                  </span>
                </div>
              )}
            </div>
            <div
              className={cn(
                'text-right font-mono font-bold tracking-tight text-base',
                qtyColor,
              )}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {signedQty}
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-[110px_1fr_70px] gap-3 px-3 py-3 text-base bg-brix-surface-alt/40 border-t border-brix-border items-center">
        <div className="col-span-2 text-slate-900 font-semibold tracking-tight">
          Current balance
        </div>
        <div
          className="text-right font-mono font-bold text-slate-900 tracking-tight"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {row.currentBalance}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- modals ----------------------------- */

function useEscapeClose(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

function ModalShell({
  ariaLabelledBy,
  onClose,
  title,
  children,
}: {
  ariaLabelledBy: string;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  useEscapeClose(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-card-deep overflow-hidden border-2 border-brix-primary-deep flex flex-col max-h-[90vh]">
        <div className="bg-brix-primary-deep px-6 md:px-8 py-4 flex items-start justify-between gap-3 shrink-0">
          <h2
            id={ariaLabelledBy}
            className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-white hover:bg-white/15 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto scrollbar-thin text-base text-slate-900 tracking-tight leading-relaxed space-y-5">
          {children}
        </div>
        <div className="px-6 md:px-8 py-4 border-t border-brix-border bg-brix-surface-alt/40 flex justify-end shrink-0">
          <Button type="button" onClick={onClose}>
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
}

function TermsHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="font-display text-base font-bold tracking-tight text-slate-900 mt-1">
      {children}
    </h3>
  );
}

function TermsModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell
      ariaLabelledBy="cylinder-terms-heading"
      onClose={onClose}
      title="Gas Cylinder Rental Terms & Conditions"
    >
      {/* Why this fee exists */}
      <section className="rounded-xl bg-brix-primary-tint/40 border border-brix-primary/20 px-4 py-4">
        <TermsHeading>Why You're Charged a Cylinder Rental</TermsHeading>
        <p className="mt-2">
          The monthly cylinder rental on your invoice is a <strong>demurrage
          fee</strong> — it isn't a profit line item. It pays for the
          safety and upkeep of every tank you have on site: paint,
          valves, seals, fittings, hydrostatic testing, and the regular
          re-certification every cylinder is required to pass.
        </p>
        <p className="mt-2">
          Per <strong>U.S. Department of Transportation (DOT)</strong>{' '}
          regulations, each high-pressure cylinder must be re-certified
          by an authorized cylinder requalification facility on a fixed
          schedule (typically every 4–5 years depending on the cylinder
          type). A cylinder past its requalification date legally cannot
          be filled and is not safe to use. The demurrage fee is what
          keeps that program funded so the tank in your back room is
          always certified, sealed, and safe.
        </p>
      </section>

      <section className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-4">
        <TermsHeading>Fire Code &amp; CO₂ Storage Permits</TermsHeading>
        <p className="mt-2">
          If you have <strong>more than 100 lbs of CO₂ on premises</strong>{' '}
          at any time, you are required to file a permit with your local
          fire department and meet additional storage and monitoring
          requirements. <strong>5 of our 20-lb tanks already crosses
          that threshold.</strong>
        </p>
        <p className="mt-2">
          Under the <em>California Fire Code (CFC) Section 5307</em> and
          the <em>International Fire Code (IFC) Section 5307</em>, indoor
          storage of more than 100 lbs of CO₂ used for beverage
          dispensing triggers requirements for: a fire-department-issued
          operational permit, ventilation or CO₂ monitoring/alarm
          systems where required, signage, and proper cylinder securing.
          Operating above the threshold without a permit can result in
          citations, required equipment modifications at your expense,
          and — in enclosed spaces — a real safety risk to your staff.
        </p>
        <p className="mt-2">
          <strong>If you need to keep more than 100 lbs of CO₂ on
          site, Brix Beverage can permit and certify you.</strong> Our
          flat fee of <strong>$2,500</strong> includes: pulling the fire
          department permit, the CO₂ safety course for your staff, and
          installation of CO₂ leak / level safety alarms. Call us at{' '}
          <a
            href="tel:+18003725098"
            className="text-brix-primary font-semibold underline"
          >
            1-800-372-5098
          </a>{' '}
          to start the process.
        </p>
      </section>

      <section>
        <TermsHeading>1. Cylinder Ownership</TermsHeading>
        <p className="mt-2">
          All CO₂ and Nitrogen cylinders supplied by the Company remain
          the exclusive property of the Company at all times. Customer
          is granted a limited right of use for the sole purpose of
          operating beverage dispensing or related equipment at the
          service address on file.
        </p>
        <p className="mt-2">
          Customer shall not transfer, loan, relocate, sublease, or
          permit the use of cylinders by any third party without the
          prior written authorization of the Company.
        </p>
        <p className="mt-2">
          Customer assumes full responsibility for the safekeeping,
          proper use, and return of all cylinders in its possession.
        </p>
      </section>

      <section>
        <TermsHeading>2. Rental Terms</TermsHeading>
        <p className="mt-2">
          Cylinder rental fees are billed monthly and will appear on
          each invoice for the duration that cylinders remain assigned
          to the Customer's account.
        </p>
        <p className="mt-2">Rental charges continue until cylinders are:</p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Physically returned to the Company</li>
          <li>Documented as picked up by the Company</li>
          <li>Otherwise reconciled through a process approved by the Company</li>
        </ul>
      </section>

      <section>
        <TermsHeading>3. Delivery &amp; Signature Verification</TermsHeading>
        <p className="mt-2">
          At the time of each delivery or pickup, the Company will
          generate a service ticket documenting the number of cylinders
          delivered and collected. A Customer representative is required
          to sign the service ticket, confirming that the quantities
          recorded are accurate. Signed service tickets constitute the
          verified record of each transaction.
        </p>
      </section>

      <section>
        <TermsHeading>4. Unattended Deliveries</TermsHeading>
        <p className="mt-2">
          If Customer requests or permits deliveries to be completed
          without a signature (unattended drop-off), Customer
          acknowledges and agrees that:
        </p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Signature verification of cylinder quantities is waived for those deliveries</li>
          <li>Cylinder counts recorded by the Company's driver shall serve as the official record of the transaction</li>
          <li>Customer waives the right to dispute quantities based on the absence of a signature</li>
        </ul>
        <p className="mt-2">
          In the event of recurring discrepancies on accounts utilizing
          unattended delivery, the Company reserves the right to require
          signature-verified deliveries as a condition of continued
          service.
        </p>
      </section>

      <section>
        <TermsHeading>5. Invoice Review &amp; Dispute Window</TermsHeading>
        <p className="mt-2">
          Cylinder rental charges appear on each monthly invoice.
          Customer is responsible for reviewing all invoices and
          verifying cylinder counts upon receipt.
        </p>
        <p className="mt-2">
          Any discrepancy regarding cylinder quantities must be reported
          to the Company in writing within <strong>ninety (90) days</strong>{' '}
          of the invoice date. Failure to report a discrepancy within
          this period constitutes acceptance of the invoice and all
          cylinder quantities billed.
        </p>
      </section>

      <section>
        <TermsHeading>6. Record Retention &amp; Credit Limitations</TermsHeading>
        <p className="mt-2">
          The Company maintains delivery records and signed service
          tickets for <strong>six (6) months</strong> from the date of
          service. Due to this retention schedule, no credits,
          adjustments, or billing corrections will be issued for
          discrepancies reported more than six (6) months after the
          applicable invoice date.
        </p>
        <p className="mt-2">
          If a discrepancy is identified within the allowable review
          period and confirmed by the Company, the Company may issue a
          credit of up to six (6) months of cylinder rental charges. Any
          such credits may be applied against outstanding replacement
          fees.
        </p>
      </section>

      <section>
        <TermsHeading>7. Lost or Missing Cylinders</TermsHeading>
        <p className="mt-2">
          Any cylinder that cannot be returned, located, or accounted
          for will be deemed lost. Lost or missing cylinders will be
          billed at the replacement fee set forth in the Company's
          current fee schedule (currently <strong>$125.00 per cylinder</strong>).
          The Company reserves the right to update the replacement fee
          upon thirty (30) days' written notice to Customer.
        </p>
      </section>

      <section>
        <TermsHeading>8. Inventory Reconciliation</TermsHeading>
        <p className="mt-2">
          The Company reserves the right to perform periodic cylinder
          inventory reconciliations at Customer's location. During any
          reconciliation:
        </p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Cylinder counts will be physically verified</li>
          <li>Cylinders not present at the time of reconciliation may be billed as lost</li>
          <li>Account records will be adjusted based on verified counts</li>
        </ul>
        <p className="mt-2">
          The Company may conduct reconciliation during any scheduled
          service visit. Customer agrees to provide reasonable access to
          stored cylinders for this purpose.
        </p>
      </section>

      <section>
        <TermsHeading>9. Policy Enforcement &amp; Remedies</TermsHeading>
        <p className="mt-2">
          Accounts with recurring cylinder discrepancies may be subject
          to additional controls at the Company's discretion, including:
        </p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Mandatory signature-verified deliveries</li>
          <li>Mandatory inventory reconciliation</li>
          <li>Modifications to delivery frequency or procedures</li>
        </ul>
        <p className="mt-2">
          Continued failure to comply with the terms of this Agreement
          may result in suspension or termination of cylinder service.
        </p>
      </section>

      <p className="text-xs text-slate-900/70 italic pt-2 border-t border-brix-border">
        Last updated 2026. Brix Beverage reserves the right to amend
        these terms. The most current version is always available here
        on your portal.
      </p>
    </ModalShell>
  );
}

function FaqModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell
      ariaLabelledBy="cylinder-faq-heading"
      onClose={onClose}
      title="Cylinder Rental FAQ"
    >
      <div className="space-y-4">
        <FaqItem
          q="Why am I paying a monthly fee even when no cylinder was delivered this month?"
          a="Because you have tanks on site. The fee covers safety upkeep, paint and seal replacement, and DOT-mandated re-certification — costs that accrue every month regardless of whether a delivery happens. Return tanks you aren't using and the fee drops with them on the next month's invoice."
        />
        <FaqItem
          q="Is keeping unused tanks costing me money?"
          a="Yes. Tanks left idle for months or years still incur the monthly rental and still count toward your fire-code threshold. They also lose value through corrosion and shifting certification dates. If you aren't using a cylinder, ask us to pick it up — it's free."
        />
        <FaqItem
          q="I think I have 6 × 20-lb CO₂ tanks. Do I need a permit?"
          a="Yes — 6 × 20 lb = 120 lbs of CO₂, over the 100-lb threshold. You should be permitted with your local fire department. Brix can handle the permit, training, and alarm install for a flat $2,500."
        />
        <FaqItem
          q="An invoice from 8 months ago looks wrong. Can you credit it?"
          a="We can only research discrepancies within 6 months of the invoice date (T&C Section 6). After that, our signed delivery tickets aren't on file to verify against, and we can't issue a credit."
        />
        <FaqItem
          q="What happens if a tank goes missing?"
          a="Lost cylinders are billed at the current replacement fee ($125 today, per T&C Section 7). Telling us right away helps — sometimes a tank is just at a sister location or got picked up by another driver and we can reconcile."
        />
        <FaqItem
          q="How much soda does a 20-lb CO₂ tank pour?"
          a="Rule of thumb for fountain systems: 1 lb of CO₂ per 7–10 gallons of finished product. (Theoretical is ~16 gal/lb at 4 volumes of carbonation, but real-world losses from carbonator inefficiency, purging, and pushing syrup cut that roughly in half.) So a 20-lb tank gives you 140–200 gallons of soda. At 18 gallons of finished product per 3-gallon BIB, that's roughly 8–11 BIBs — use ~10 as the safe planning number. Big swing factors: whether the CO₂ also drives the syrup pumps (knocks you toward 8), water temperature at the carbonator, and any system leaks — a slow leak can easily cut a tank's yield in half."
        />
      </div>
    </ModalShell>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface-alt/30 px-4 py-3">
      <p className="font-semibold text-slate-900 tracking-tight">{q}</p>
      <p className="mt-1.5 leading-relaxed">{a}</p>
    </div>
  );
}

/**
 * A chain's corporate login standing on the master gets EVERY store's cylinders
 * instead of the master account's own handful. Everyone else — a store's own
 * people, and the same person once they pick a store — gets the page above,
 * unchanged.
 *
 * ⚠ A WRAPPER, not a branch inside the page. Returning early above that
 * component's own hooks would change hook order between renders.
 */
export default function Cylinders() {
  const { data: group, isLoading } = useGroupAssets();

  // Hold the single-account render until we know which page this is. A flash
  // of corporate's own empty cylinders before the chain view lands reads as
  // "where did everything go".
  if (isLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <ChainSwitchSpinner size={16} className="animate-spin inline-block mr-2" />
        Loading your cylinders…
      </div>
    );
  }
  if (group?.is_group) return <ChainCylinders />;
  return <SingleCylinders />;
}
